import { action, computed, observable, runInAction } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';
import { toast, ToastContainer } from 'react-toastify';

import { WithDbId } from '../../../../../adl-gen/common/db';
import { AppUser } from '../../../../../adl-gen/totalinteraction/futurefit/db';
import { AppUserListEntry } from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { UserData } from '../../../../../adl-gen/totalinteraction/futurefit/requests';
import { AppUserStore, UserGroupChange, UserQuizChange } from '../../../../stores';
import { SelectMode, toastOptions } from '../../../common/form';
import { Message } from '../../../widgets/message/message';
import { AdminModal } from '../../admin-modal/admin-modal';
import { AppUserEditor } from '../../editors/appuser-editor/appuser-editor';

// tslint:disable-next-line: no-import-side-effect ordered-imports
import '../../../widgets/toast/toastify.css';

interface AppUserFormProps {
  user?: AppUserListEntry;
  userData: UserData;
  store: AppUserStore;
  /// TODO: retire this
  /** @deprecated Stop using modals */
  selectMode: SelectMode;
  onCancel(): void;
}

@observer
export class AppUserForm extends React.Component<AppUserFormProps> {
  /// Changes
  @observable groupChangeList: UserGroupChange[] = new Array<UserGroupChange>();

  /** @deprecated */
  @observable purgeOk = false;

  @observable isOpen = false;

  @action
  /** @deprecated */
  checkSafeUpdate = () => {
    this.lockPurge();
    this.isOpen = true;
  };

  @action
  /** @deprecated */
  lockPurge = () => {
    this.purgeOk = false;
  };

  @action
  /** @deprecated */
  unlockPurge = () => {
    this.purgeOk = true;
  };

  @computed
  /** @deprecated */
  get purgeGuardCleared() {
    return this.purgeOk;
  }

  @action
  cancelUpdate = () => {
    this.isOpen = false;
    this.props.onCancel();
  };

  @action
  continueUpdate = () => {
    this.isOpen = false;
    this.unlockPurge();
    // TODO: Call onUpdate or onCreate HERE - no need for purge guards
  };

  // TODO: Sort out MobX and promise chain
  onSafeCreate = async (
    item: AppUser,
    addToGlobalGroup: boolean,
    groupUpdates: UserGroupChange[],
    userQuizUpdates: UserQuizChange[]
  ): Promise<void> => {
    /// Check for purges
    await runInAction(async () => {
      const purges = userQuizUpdates.filter(upd => upd.kind === 'purge');
      if (purges.length > 0) {
        this.checkSafeUpdate();
        // TODO: Cancel or continue controlled by buttons in modal message
        // if (this.purgeGuardCleared) {
        //   await this.onCreate(item, addToGlobalGroup, userQuizUpdates)
        // }
      } else {
        await this.onCreate(item, addToGlobalGroup, groupUpdates, userQuizUpdates);
      }
    });
  };

  // TODO: Sort out MobX and promise chain
  onSafeUpdate = async (
    item: WithDbId<AppUser>,
    groupUpdates: UserGroupChange[],
    userQuizUpdates: UserQuizChange[],
    reactivating?: boolean // undefined = no change, true = reactivated, false = suspended
  ) => {
    /// Check for purges
    await runInAction(async () => {
      const purges = userQuizUpdates.filter(upd => upd.kind === 'purge');
      if (purges.length > 0) {
        this.checkSafeUpdate();
        // TODO: Cancel or continue controlled by buttons in modal message
        // if (this.purgeGuardCleared) {
        //   console.debug("DEBUG ok to go")
        //   await this.onUpdate(item, userQuizUpdates, reactivating)
        // }
      } else {
        await this.onUpdate(item, groupUpdates, userQuizUpdates, reactivating);
      }
    });
  };

  onCreate = async (
    item: AppUser,
    addToGlobalGroup: boolean,
    groupUpdates: UserGroupChange[],
    userQuizUpdates: UserQuizChange[]
  ): Promise<void> => {
    const { onCancel, store } = this.props;

    const toastId = toast.loading('Please wait...', { ...toastOptions, autoClose: 3000 });
    try {
      const userId = addToGlobalGroup ? await store.createWithGroup(item) : await store.create(item);

      await store.updateUserGroups(userId, groupUpdates);
      await store.updateUserQuizzes(userId, userQuizUpdates);

      toast.dismiss(toastId);
      toast.success(`User ${item.fullname} created.`, { ...toastOptions, onClose: onCancel, autoClose: 3000 });
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(`User ${item.fullname} create failed: ${(e as Error).message}`, {
        ...toastOptions,
        onClose: onCancel
      });
    }
  };

  onUpdate = async (
    item: WithDbId<AppUser>,
    groupUpdates: UserGroupChange[],
    userQuizUpdates: UserQuizChange[],
    reactivating?: boolean // undefined = no change, true = reactivated, false = suspended
  ) => {
    const { onCancel, store } = this.props;

    const toastId = toast.loading('Please wait...', { ...toastOptions, autoClose: 3000 });
    try {
      await store.update(item);

      await store.updateUserGroups(item.id, groupUpdates);
      await store.updateUserQuizzes(item.id, userQuizUpdates);

      toast.dismiss(toastId);
      toast.success(
        `User ${item.value.fullname} updated. 
       ${reactivating === undefined ? '' : reactivating ? ' User activated.' : ' User suspended.'}`,
        { ...toastOptions, onClose: onCancel, autoClose: 3000 }
      );
      // toast.success(`User ${item.value.fullname} updated.
      //  ${reactivate === undefined ? "" : reactivate ? " User activated." : " User suspended."}`,
      //   {...toastOptions, onClose: onCancel })
    } catch (e) {
      toast.dismiss(toastId);
      toast.error(`User ${item.value.fullname} update failed: ${(e as Error).message}`, {
        ...toastOptions,
        onClose: onCancel
      });
    }
  };

  // onDeletes = async (a: DbKey<AppClient>[]) => {
  //   try {
  //     await this.props.onDelete(a)
  //     runInAction(() => (this.fieldState.deletedClients = a.slice()))
  //   } catch (e) {
  //     runInAction(() => (this.fieldState.error = e as Error))
  //   }
  // }

  @action
  onCancel = () => {
    this.isOpen = false;
    this.props.onCancel();
  };

  @action
  showPurgeWarning = () => {
    return (
      <Message
        kind="error"
        title="Warning"
        body="There are records associated with this user assessment. If you continue, all results will be lost."
        button1={{
          label: 'Go back',
          onClick: async () => this.cancelUpdate()
        }}
        button2={{
          label: 'Continue',
          onClick: async () => this.continueUpdate()
        }}
      />
    );
  };

  renderContent = () => {
    const {
      user,
      userData,
      // mode,
      store
      // toDelete,
    } = this.props;
    if (
      // mode === "update" &&
      user
    ) {
      return (
        <AppUserEditor
          item={user}
          data={userData}
          store={store}
          onCancel={this.onCancel}
          onCreate={this.onCreate}
          onUpdate={this.onUpdate}
          applicationRole={store.isSuperUser ? 'super' : 'client-admin'}
        />
      );
      /// TODO: Delete
    } else {
      return (
        <AppUserEditor
          data={userData}
          store={store}
          onCancel={this.onCancel}
          onCreate={this.onCreate}
          applicationRole={store.isSuperUser ? 'super' : 'client-admin'}
        />
      );
    }
  };

  renderMessageModal(content: JSX.Element) {
    return (
      <AdminModal
        message
        isOpen={this.isOpen}
        onRequestClose={this.onCancel}
        shouldCloseOnOverlayClick={false}
        shouldCloseOnEsc={false}
      >
        {content}
      </AdminModal>
    );
  }

  render() {
    return (
      <React.Fragment>
        {/* {this.props.selectMode === "modal" ? this.renderModal() :  */}
        {this.renderMessageModal(this.showPurgeWarning())}
        {this.renderContent()}
        <ToastContainer newestOnTop />
      </React.Fragment>
    );
  }
}



// WEBPACK FOOTER //
// ./src/admin/ui/components/forms/appuser-form/appuser-form.tsx